/**
 * LoginPage — вход / регистрация в ЛК родителя.
 *
 * Email-first flow без user enumeration:
 * 1. Ввод email → зелёная стрелка → всегда одинаковая анимация
 * 2. Появляется поле пароля + «Войти» + ссылка «Зарегистрироваться»
 * 3. При клике «Зарегистрироваться» → пароль + повтор + кнопка
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Container, Paper, Stack, Text, Title, TextInput, PasswordInput,
  Button, Center, ActionIcon, Loader, Transition, Checkbox,
} from '@mantine/core';
import { IconMail, IconLock, IconArrowRight } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { useParentSession } from '../context/ParentSessionContext';
import tokens from '../theme/designTokens';

const LoginPage = () => {
  const navigate = useNavigate();
  const { login } = useParentSession();

  // Steps: 'email' → 'login' → 'register'
  const [step, setStep] = useState('email');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [animating, setAnimating] = useState(false);
  const [consent, setConsent] = useState(false);

  // Зелёная стрелка — переход к вводу пароля (одинаковая анимация, без проверки)
  const handleEmailSubmit = (e) => {
    e?.preventDefault();
    if (!email.trim() || !email.includes('@')) {
      notifications.show({ title: 'Ошибка', message: 'Введите корректный email', color: 'red' });
      return;
    }
    setAnimating(true);
    // Одинаковая задержка 500ms — нельзя определить есть ли email в системе
    setTimeout(() => {
      setAnimating(false);
      setStep('login');
    }, 500);
  };

  // Вход
  const handleLogin = async (e) => {
    e?.preventDefault();
    if (!password) return;
    setSubmitting(true);
    try {
      await login(email, password);
      navigate('/');
    } catch {
      // Generic сообщение — не раскрываем причину
      notifications.show({
        title: 'Ошибка входа',
        message: 'Неверный email или пароль',
        color: 'red',
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Регистрация
  const handleRegister = async (e) => {
    e?.preventDefault();
    if (!password || password.length < 8) {
      notifications.show({ title: 'Ошибка', message: 'Пароль должен быть не менее 8 символов', color: 'red' });
      return;
    }
    if (!(/[a-zA-Zа-яА-Я]/.test(password) && /\d/.test(password))) {
      notifications.show({ title: 'Ошибка', message: 'Пароль должен содержать буквы и цифры', color: 'red' });
      return;
    }
    if (password !== passwordConfirm) {
      notifications.show({ title: 'Ошибка', message: 'Пароли не совпадают', color: 'red' });
      return;
    }

    setSubmitting(true);
    try {
      // Регистрация
      const regRes = await fetch('/api/parents/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password, privacy_consent: true }),
      });
      if (!regRes.ok) {
        const err = await regRes.json();
        throw new Error(err.detail || 'Не удалось зарегистрироваться');
      }
      // Автологин после регистрации
      await login(email, password);
      navigate('/');
    } catch (err) {
      notifications.show({
        title: 'Ошибка',
        message: err.message || 'Не удалось зарегистрироваться. Если у вас уже есть аккаунт, попробуйте войти.',
        color: 'red',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box style={{ minHeight: '100vh', background: tokens.colors.bg.page, paddingTop: 60 }}>
      <Container size="xs">
        {/* Логотип */}
        <Center mb="xl">
          <Box style={{ textAlign: 'center' }}>
            <Box
              style={{
                width: 80,
                height: 80,
                borderRadius: 24,
                background: tokens.gradients.hero,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontFamily: tokens.typography.fontFamilyHeading,
                fontWeight: 1000,
                fontSize: 32,
                boxShadow: '0 12px 32px rgba(124,58,237,0.3)',
                marginBottom: 16,
              }}
            >
              ЭИ
            </Box>
            <Title
              order={2}
              style={{
                fontFamily: tokens.typography.fontFamilyHeading,
                fontWeight: 1000,
                color: tokens.colors.text.primary,
              }}
            >
              Это <span style={{ color: tokens.colors.brand.orange }}>Интересно!</span>
            </Title>
            <Text c={tokens.colors.text.muted} fw={700} mt={4}>
              Личный кабинет родителя
            </Text>
          </Box>
        </Center>

        <Paper p="xl" radius="xl" shadow="md" style={{ background: 'white' }}>
          <Stack gap="md">

            {/* ═══ Шаг 1: Email ═══ */}
            {step === 'email' && (
              <form onSubmit={handleEmailSubmit}>
                <Stack gap="md">
                  <Box>
                    <Title order={3} mb={4} style={{ color: tokens.colors.text.primary }}>
                      Добро пожаловать
                    </Title>
                    <Text size="sm" c={tokens.colors.text.muted} fw={700}>
                      Введите email для входа или регистрации
                    </Text>
                  </Box>

                  <Box style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                    <TextInput
                      placeholder="parent@example.com"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      leftSection={<IconMail size={16} />}
                      required
                      size="md"
                      radius="xl"
                      style={{ flex: 1 }}
                    />
                    <ActionIcon
                      type="submit"
                      size="xl"
                      radius="xl"
                      variant="filled"
                      color="green"
                      loading={animating}
                    >
                      <IconArrowRight size={20} />
                    </ActionIcon>
                  </Box>
                </Stack>
              </form>
            )}

            {/* ═══ Шаг 2: Вход ═══ */}
            {step === 'login' && (
              <form onSubmit={handleLogin}>
                <Stack gap="md">
                  <Box>
                    <Title order={3} mb={4} style={{ color: tokens.colors.text.primary }}>
                      Вход в кабинет
                    </Title>
                  </Box>

                  <Box style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                    <TextInput
                      value={email}
                      leftSection={<IconMail size={16} />}
                      size="md"
                      radius="xl"
                      style={{ flex: 1 }}
                      readOnly
                      styles={{ input: { color: tokens.colors.text.muted } }}
                    />
                    <ActionIcon
                      size="xl"
                      radius="xl"
                      variant="filled"
                      color="green"
                      onClick={() => { setStep('email'); setPassword(''); setPasswordConfirm(''); }}
                    >
                      <IconArrowRight size={20} style={{ transform: 'rotate(180deg)' }} />
                    </ActionIcon>
                  </Box>

                  <PasswordInput
                    label="Пароль"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    leftSection={<IconLock size={16} />}
                    required
                    size="md"
                    radius="xl"
                  />

                  <Button
                    type="submit"
                    fullWidth
                    size="lg"
                    loading={submitting}
                    color="brand-orange"
                  >
                    Войти
                  </Button>

                  <Text
                    size="sm"
                    ta="center"
                    fw={700}
                    style={{ color: tokens.colors.brand.purple, cursor: 'pointer' }}
                    onClick={() => { setStep('register'); setPassword(''); setPasswordConfirm(''); }}
                  >
                    Нет аккаунта? Зарегистрироваться
                  </Text>
                </Stack>
              </form>
            )}

            {/* ═══ Шаг 3: Регистрация ═══ */}
            {step === 'register' && (
              <form onSubmit={handleRegister}>
                <Stack gap="md">
                  <Box>
                    <Title order={3} mb={4} style={{ color: tokens.colors.text.primary }}>
                      Регистрация
                    </Title>
                    <Text size="sm" c={tokens.colors.text.muted} fw={700}>
                      Создайте аккаунт для доступа к личному кабинету
                    </Text>
                  </Box>

                  <TextInput
                    label="Email"
                    value={email}
                    leftSection={<IconMail size={16} />}
                    size="md"
                    radius="xl"
                    disabled
                  />

                  <PasswordInput
                    label="Пароль"
                    description="Минимум 8 символов, буквы и цифры"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    leftSection={<IconLock size={16} />}
                    required
                    size="md"
                    radius="xl"
                  />

                  <PasswordInput
                    label="Повторите пароль"
                    placeholder="••••••••"
                    value={passwordConfirm}
                    onChange={(e) => setPasswordConfirm(e.target.value)}
                    leftSection={<IconLock size={16} />}
                    required
                    size="md"
                    radius="xl"
                  />

                  <Checkbox
                    checked={consent}
                    onChange={(e) => setConsent(e.currentTarget.checked)}
                    radius="xl"
                    size="sm"
                    label={
                      <Text size="xs" c={tokens.colors.text.muted}>
                        Нажимая кнопку &laquo;Зарегистрироваться&raquo;, я даю согласие на обработку
                        персональных данных согласно Политике конфиденциальности компании
                      </Text>
                    }
                  />

                  <Button
                    type="submit"
                    fullWidth
                    size="lg"
                    loading={submitting}
                    disabled={!consent}
                    color="brand-orange"
                  >
                    Зарегистрироваться
                  </Button>

                  <Text
                    size="sm"
                    ta="center"
                    fw={700}
                    style={{ color: tokens.colors.brand.purple, cursor: 'pointer' }}
                    onClick={() => { setStep('login'); setPassword(''); setPasswordConfirm(''); setConsent(false); }}
                  >
                    Уже есть аккаунт? Войти
                  </Text>
                </Stack>
              </form>
            )}

          </Stack>
        </Paper>
      </Container>
    </Box>
  );
};

export default LoginPage;
